import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardService } from "../../services/DashboardService";
import { logger } from "../../utils/logger";

// Query Keys
export const dashboardKeys = {
  all: ["dashboards"],
  lists: () => [...dashboardKeys.all, "list"],
  list: (orgId) => [...dashboardKeys.lists(), orgId],
  details: () => [...dashboardKeys.all, "detail"],
  detail: (id) => [...dashboardKeys.details(), id],
};

/**
 * Hook to fetch dashboards for an organization
 * @param {string} orgId - Organization ID
 * @returns {object} Query result with dashboards data
 */
export function useDashboards(orgId) {
  return useQuery({
    queryKey: dashboardKeys.list(orgId || ""),
    queryFn: async () => {
      if (!orgId) {
        return [];
      }
      try {
        const response = await DashboardService.fetchDashboards(orgId);
        return response || [];
      } catch (error) {
        logger.error("Error fetching dashboards:", error);
        throw error; // Let React Query handle the error
      }
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useShareDashboard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dashboardId) => DashboardService.shareDashboard(dashboardId),
    onSuccess: (_, dashboardId) => {
      // Invalidate the specific dashboard to refresh sharing status
      queryClient.invalidateQueries({
        queryKey: dashboardKeys.detail(dashboardId),
      });
      // Also invalidate the list to refresh any summary data
      queryClient.invalidateQueries({ queryKey: dashboardKeys.lists() });
    },
  });
}
